import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Loader2,
  PoundSterling,
  TrendingUp,
  Calendar,
  Star,
  CreditCard,
  Crown,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Lock,
  Unlock,
  Mail,
  Plus,
  Trash2,
  Settings,
  Users,
  Dumbbell,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  History
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, booking: null });
  const [disputeDialog, setDisputeDialog] = useState({ open: false, dispute: null });
  const [statusNotes, setStatusNotes] = useState('');
  const [disputeResponse, setDisputeResponse] = useState('');
  const [updating, setUpdating] = useState(false);
  const [unlocking, setUnlocking] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [sessionOfferings, setSessionOfferings] = useState([]);
  const [savingPricing, setSavingPricing] = useState(false);
  const [packageOfferings, setPackageOfferings] = useState([]);
  const [savingPackages, setSavingPackages] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [savingSubscriptions, setSavingSubscriptions] = useState(false);
  
  // TRAINR Subscription state
  const [trainrSubscription, setTrainrSubscription] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  
  // Coaching state
  const [coachingClients, setCoachingClients] = useState([]);
  const [coachingWorkouts, setCoachingWorkouts] = useState([]);
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientNotes, setNewClientNotes] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [showWorkoutBuilder, setShowWorkoutBuilder] = useState(false);
  const [workoutName, setWorkoutName] = useState('');
  const [workoutExercises, setWorkoutExercises] = useState([{ name: '', sets: 3, target_reps: 10, target_weight: '', notes: '' }]);
  const [savingWorkout, setSavingWorkout] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [assigningWorkout, setAssigningWorkout] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [dashboardRes, stripeRes, analyticsRes, sessionsRes, offeringsRes, packagesRes, availRes, subsRes, coachingClientsRes, coachingWorkoutsRes, trainrSubRes] = await Promise.all([
        axios.get(`${API_URL}/trainer/dashboard`),
        axios.get(`${API_URL}/trainer/stripe-connect/status`),
        axios.get(`${API_URL}/trainer/analytics`),
        axios.get(`${API_URL}/trainer/sessions`),
        axios.get(`${API_URL}/trainer/my-offerings`),
        axios.get(`${API_URL}/trainer/my-packages`),
        axios.get(`${API_URL}/trainer/my-availability`),
        axios.get(`${API_URL}/trainer/my-subscriptions`),
        axios.get(`${API_URL}/coaching/clients`).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API_URL}/coaching/workouts`).catch(() => ({ data: { workouts: [] } })),
        axios.get(`${API_URL}/trainer/subscription-status`).catch(() => ({ data: null }))
      ]);
      setDashboardData(dashboardRes.data);
      setStripeStatus(stripeRes.data);
      setAnalytics(analyticsRes.data);
      setSessions(sessionsRes.data.sessions || []);
      setIsPremium(sessionsRes.data.is_premium || false);
      setSessionOfferings(offeringsRes.data.session_offerings || []);
      setPackageOfferings(packagesRes.data.package_offerings || []);
      setAvailability(availRes.data.availability || []);
      setSubscriptionPlans(subsRes.data.subscription_plans || []);
      setCoachingClients(coachingClientsRes.data.clients || []);
      setCoachingWorkouts(coachingWorkoutsRes.data.workouts || []);
      setTrainrSubscription(trainrSubRes.data);
      
      // Filter disputes that need trainer response
      const pendingDisputes = (sessionsRes.data.sessions || []).filter(
        s => s.dispute_status === 'pending_trainer_response'
      );
      setDisputes(pendingDisputes);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 404) {
        toast.error('Trainer profile not found. Please apply to become a trainer first.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    
    // Check URL params for Stripe redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_success') === 'true') {
      toast.success('Stripe account setup successful!');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe setup');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Handle unlock success redirect
    if (params.get('unlock_success')) {
      toast.success('Client unlocked! All future sessions with this client are free to access.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('unlock_cancelled') === 'true') {
      toast.info('Lead unlock cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchDashboardData]);

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const response = await axios.post(`${API_URL}/trainer/stripe-connect/onboard`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      toast.error('Failed to start Stripe setup');
      setConnectingStripe(false);
    }
  };

  const handleUpgradePremium = async (plan = 'pro_monthly') => {
    setSubscribing(true);
    try {
      const response = await axios.post(`${API_URL}/trainer/subscribe`, {
        plan: plan,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription checkout');
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will keep your benefits until the end of the billing period.')) {
      return;
    }
    try {
      await axios.post(`${API_URL}/trainer/cancel-subscription`);
      toast.success('Subscription will be cancelled at the end of the billing period');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handleUpdateSessionStatus = async (bookingId, status) => {
    setUpdating(true);
    try {
      await axios.post(`${API_URL}/trainer/sessions/${bookingId}/status`, {
        status,
        notes: statusNotes
      });
      toast.success(`Session marked as ${status.replace('_', ' ')}`);
      setStatusDialog({ open: false, booking: null });
      setStatusNotes('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update session status');
    } finally {
      setUpdating(false);
    }
  };

  const handleRespondToDispute = async (disputeId) => {
    if (!disputeResponse.trim()) {
      toast.error('Please enter your response');
      return;
    }
    setUpdating(true);
    try {
      await axios.post(`${API_URL}/trainer/disputes/${disputeId}/respond?response=${encodeURIComponent(disputeResponse)}`);
      toast.success('Response submitted successfully');
      setDisputeDialog({ open: false, dispute: null });
      setDisputeResponse('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Awaiting Session' },
      completed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Completed' },
      user_no_show: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'User No-Show' },
      trainer_missed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Trainer Missed' },
      disputed: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Disputed' },
      cancelled: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Cancelled' },
      refunded: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Refunded' }
    };
    const config = statusConfig[status] || { bg: 'bg-gray-500/20', text: 'text-gray-400', label: status };
    return <Badge className={`${config.bg} ${config.text}`}>{config.label}</Badge>;
  };

  const handleUnlockLead = async (userId) => {
    setUnlocking(userId);
    try {
      const response = await axios.post(`${API_URL}/trainer/leads/unlock`, {
        user_id: userId,
        origin_url: window.location.origin
      });
      
      if (response.data.already_unlocked) {
        // Client was already unlocked - no charge
        toast.success('This client is already unlocked! All sessions are free to access.');
        fetchDashboardData();
      } else if (response.data.checkout_url) {
        // Redirect to Stripe checkout for non-premium
        window.location.href = response.data.checkout_url;
      } else {
        // Premium trainer - auto unlocked
        toast.success('Client unlocked! All future sessions with this client are free to access.');
        fetchDashboardData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock lead');
    } finally {
      setUnlocking(null);
    }
  };

  // Session Pricing Management
  const addSessionOffering = () => {
    const newOffering = {
      id: `offering_${Date.now()}`,
      duration: 60,
      price: 40,
      label: ''
    };
    setSessionOfferings([...sessionOfferings, newOffering]);
  };

  const updateOffering = (index, field, value) => {
    const updated = [...sessionOfferings];
    updated[index] = { ...updated[index], [field]: value };
    setSessionOfferings(updated);
  };

  const removeOffering = (index) => {
    setSessionOfferings(sessionOfferings.filter((_, i) => i !== index));
  };

  const saveSessionPricing = async () => {
    if (sessionOfferings.length === 0) {
      toast.error('Please add at least one session offering');
      return;
    }
    
    // Validate offerings
    for (const offering of sessionOfferings) {
      if (offering.duration < 15 || offering.duration > 180) {
        toast.error('Session duration must be between 15 and 180 minutes');
        return;
      }
      if (offering.price < 5 || offering.price > 500) {
        toast.error('Session price must be between £5 and £500');
        return;
      }
    }
    
    setSavingPricing(true);
    try {
      await axios.put(`${API_URL}/trainer/my-offerings`, {
        session_offerings: sessionOfferings.map(o => ({
          id: o.id,
          duration: parseInt(o.duration),
          price: parseFloat(o.price),
          label: o.label || `${o.duration} Minutes`
        }))
      });
      toast.success('Session pricing saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  // Package Management
  const addPackage = () => {
    const newPackage = {
      id: `pkg_${Date.now()}`,
      name: '',
      session_count: 5,
      session_duration: 60,
      total_price: 200,
      savings_percent: 10
    };
    setPackageOfferings([...packageOfferings, newPackage]);
  };

  const updatePackage = (index, field, value) => {
    const updated = [...packageOfferings];
    updated[index] = { ...updated[index], [field]: value };
    setPackageOfferings(updated);
  };

  const removePackage = (index) => {
    setPackageOfferings(packageOfferings.filter((_, i) => i !== index));
  };

  const savePackages = async () => {
    setSavingPackages(true);
    try {
      await axios.put(`${API_URL}/trainer/my-packages`, {
        package_offerings: packageOfferings.map(p => ({
          id: p.id,
          name: p.name || `${p.session_count}-Session Package`,
          session_count: parseInt(p.session_count),
          session_duration: parseInt(p.session_duration),
          total_price: parseFloat(p.total_price),
          savings_percent: parseFloat(p.savings_percent || 0)
        }))
      });
      toast.success('Packages saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save packages');
    } finally {
      setSavingPackages(false);
    }
  };

  // Availability Management
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const addAvailabilitySlot = () => {
    const newSlot = {
      day_of_week: 0,
      start_time: '09:00',
      end_time: '17:00'
    };
    setAvailability([...availability, newSlot]);
  };

  const updateAvailabilitySlot = (index, field, value) => {
    const updated = [...availability];
    updated[index] = { ...updated[index], [field]: value };
    setAvailability(updated);
  };

  const removeAvailabilitySlot = (index) => {
    setAvailability(availability.filter((_, i) => i !== index));
  };

  const saveAvailability = async () => {
    setSavingAvailability(true);
    try {
      await axios.put(`${API_URL}/trainer/my-availability`, {
        availability: availability.map(s => ({
          day_of_week: parseInt(s.day_of_week),
          start_time: s.start_time,
          end_time: s.end_time
        }))
      });
      toast.success('Availability saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save availability');
    } finally {
      setSavingAvailability(false);
    }
  };

  // Subscription Plan Management
  const addSubscriptionPlan = () => {
    const newPlan = {
      id: `sub_${Date.now()}`,
      name: '',
      sessions_per_month: 4,
      session_duration: 60,
      monthly_price: 150,
      description: ''
    };
    setSubscriptionPlans([...subscriptionPlans, newPlan]);
  };

  const updateSubscriptionPlan = (index, field, value) => {
    const updated = [...subscriptionPlans];
    updated[index] = { ...updated[index], [field]: value };
    setSubscriptionPlans(updated);
  };

  const removeSubscriptionPlan = (index) => {
    setSubscriptionPlans(subscriptionPlans.filter((_, i) => i !== index));
  };

  const saveSubscriptionPlans = async () => {
    setSavingSubscriptions(true);
    try {
      await axios.put(`${API_URL}/trainer/my-subscriptions`, {
        subscription_plans: subscriptionPlans.map(p => ({
          id: p.id,
          name: p.name || `${p.sessions_per_month}x Monthly Training`,
          sessions_per_month: parseInt(p.sessions_per_month),
          session_duration: parseInt(p.session_duration),
          monthly_price: parseFloat(p.monthly_price),
          description: p.description
        }))
      });
      toast.success('Subscription plans saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subscription plans');
    } finally {
      setSavingSubscriptions(false);
    }
  };

  // Coaching Functions
  const addCoachingClient = async () => {
    if (!newClientEmail.trim()) {
      toast.error('Please enter client email');
      return;
    }
    setAddingClient(true);
    try {
      await axios.post(`${API_URL}/coaching/clients`, {
        user_email: newClientEmail.toLowerCase(),
        notes: newClientNotes
      });
      toast.success('Client added successfully!');
      setNewClientEmail('');
      setNewClientNotes('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add client');
    } finally {
      setAddingClient(false);
    }
  };

  const addExercise = () => {
    setWorkoutExercises([...workoutExercises, { name: '', sets: 3, target_reps: 10, target_weight: '', notes: '' }]);
  };

  const updateExercise = (index, field, value) => {
    const updated = [...workoutExercises];
    updated[index] = { ...updated[index], [field]: value };
    setWorkoutExercises(updated);
  };

  const removeExercise = (index) => {
    if (workoutExercises.length > 1) {
      setWorkoutExercises(workoutExercises.filter((_, i) => i !== index));
    }
  };

  const saveWorkout = async () => {
    if (!workoutName.trim()) {
      toast.error('Please enter a workout name');
      return;
    }
    if (workoutExercises.some(ex => !ex.name.trim())) {
      toast.error('Please name all exercises');
      return;
    }
    setSavingWorkout(true);
    try {
      await axios.post(`${API_URL}/coaching/workouts`, {
        name: workoutName,
        exercises: workoutExercises.map(ex => ({
          name: ex.name,
          sets: parseInt(ex.sets) || 3,
          target_reps: parseInt(ex.target_reps) || 10,
          target_weight: ex.target_weight ? parseFloat(ex.target_weight) : null,
          notes: ex.notes
        }))
      });
      toast.success('Workout created!');
      setShowWorkoutBuilder(false);
      setWorkoutName('');
      setWorkoutExercises([{ name: '', sets: 3, target_reps: 10, target_weight: '', notes: '' }]);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workout');
    } finally {
      setSavingWorkout(false);
    }
  };

  const assignWorkout = async (clientId, workoutId) => {
    setAssigningWorkout(`${clientId}-${workoutId}`);
    try {
      await axios.post(`${API_URL}/coaching/assign`, {
        client_id: clientId,
        workout_id: workoutId
      });
      toast.success('Workout assigned!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign workout');
    } finally {
      setAssigningWorkout(null);
    }
  };

  const deleteWorkout = async (workoutId) => {
    try {
      await axios.delete(`${API_URL}/coaching/workouts/${workoutId}`);
      toast.success('Workout deleted');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete workout');
    }
  };

  const fetchClientDetails = async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/coaching/clients/${clientId}`);
      setSelectedClient(response.data);
    } catch (error) {
      toast.error('Failed to load client details');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-16">
          <div className="max-w-2xl mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold uppercase text-white mb-4">
              Trainer Profile Not Found
            </h1>
            <p className="text-gray-400 mb-6">
              You need to apply and be approved as a trainer to access this dashboard.
            </p>
            <Link to="/become-a-trainer">
              <Button className="bg-primary text-white">
                Apply to Become a Trainer
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const { trainer, earnings, recent_bookings, premium } = dashboardData;

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="trainer-dashboard">
      <Header />
      
      <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter uppercase text-white">
                Welcome, {trainer.name}
              </h1>
              <p className="text-gray-400">Manage your bookings and earnings</p>
              {trainer.is_demo_account && (
                <Badge className="mt-2 bg-purple-500/20 text-purple-400 text-xs">
                  Demo Account - For demonstration only
                </Badge>
              )}
            </div>
            {/* TRAINR Subscription Status */}
            {trainrSubscription && (
              <div className="flex flex-col items-end gap-2">
                <Badge className={`text-sm px-4 py-2 ${
                  trainrSubscription.current_plan === 'pro_annual' 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400'
                    : trainrSubscription.current_plan === 'pro_monthly'
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  <Crown className="w-4 h-4 mr-2" />
                  {trainrSubscription.plan_display_name}
                </Badge>
                <span className="text-sm text-gray-400">
                  {trainrSubscription.commission_rate}% marketplace commission
                </span>
              </div>
            )}
          </div>

          {/* TRAINR Subscription Upgrade Banner - Show for Free users */}
          {trainrSubscription && trainrSubscription.current_plan === 'free' && (
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-500" />
                    Upgrade to TRAINR Pro
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Reduce your commission rate and keep more of your earnings
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleUpgradePremium('pro_monthly')}
                    disabled={subscribing}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                    data-testid="upgrade-pro-monthly"
                  >
                    {subscribing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Pro £19.99/mo (5%)
                  </Button>
                  <Button
                    onClick={() => handleUpgradePremium('pro_annual')}
                    disabled={subscribing}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold"
                    data-testid="upgrade-pro-annual"
                  >
                    {subscribing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Pro Annual £199/yr (4%)
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Pro Subscription Status */}
          {trainrSubscription && trainrSubscription.current_plan !== 'free' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-yellow-500" />
                  <div>
                    <span className="text-yellow-400 font-medium">{trainrSubscription.plan_display_name} Active</span>
                    <span className="text-gray-400 text-sm ml-2">
                      ({trainrSubscription.commission_rate}% commission)
                    </span>
                    {trainrSubscription.subscription_end && (
                      <span className="text-gray-500 text-sm ml-2">
                        • Renews {new Date(trainrSubscription.subscription_end).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                {trainrSubscription.status !== 'cancelling' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelSubscription}
                    className="border-gray-600 text-gray-400 hover:text-white"
                  >
                    Cancel Subscription
                  </Button>
                ) : (
                  <Badge className="bg-orange-500/20 text-orange-400">
                    Cancelling at period end
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Stripe Connect Alert */}
          {!stripeStatus?.payouts_enabled && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-2">Set Up Payouts</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Connect your bank account to receive earnings from your sessions. Your funds will be held until you complete this step.
                  </p>
                  <Button
                    onClick={handleConnectStripe}
                    disabled={connectingStripe}
                    className="bg-primary text-white"
                    data-testid="connect-stripe-button"
                  >
                    {connectingStripe ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    {stripeStatus?.has_account ? 'Complete Setup' : 'Connect Bank Account'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {stripeStatus?.payouts_enabled && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-green-400">Payouts enabled - You're all set to receive earnings!</span>
              </div>
            </div>
          )}

          {/* Pricing Setup Alert */}
          {sessionOfferings.length === 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-2">Set Up Your Pricing</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    You haven't set up your session pricing yet. Clients won't be able to book until you add your session offerings.
                  </p>
                  <Button
                    onClick={() => {
                      addSessionOffering();
                      // Switch to pricing tab
                      document.querySelector('[value="pricing"]')?.click();
                    }}
                    className="bg-orange-500 text-white hover:bg-orange-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Set Up Pricing Now
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-2">
                <PoundSterling className="w-5 h-5 text-primary" />
                <span className="text-gray-400 text-sm">Total Earnings</span>
              </div>
              <p className="text-3xl font-bold text-white">£{earnings.total_earnings?.toFixed(2) || '0.00'}</p>
            </div>
            
            <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <span className="text-gray-400 text-sm">Pending Payout</span>
              </div>
              <p className="text-3xl font-bold text-white">£{earnings.pending_earnings?.toFixed(2) || '0.00'}</p>
            </div>
            
            <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                <span className="text-gray-400 text-sm">Total Bookings</span>
              </div>
              <p className="text-3xl font-bold text-white">{earnings.total_bookings || 0}</p>
            </div>
            
            <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-400 text-sm">Rating</span>
              </div>
              <p className="text-3xl font-bold text-white">{trainer.rating?.toFixed(1) || '0.0'}</p>
            </div>
          </div>

          {/* Premium Benefits */}
          {premium.is_premium && (
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl p-6 border border-yellow-500/30 mb-8">
              <h3 className="font-heading text-xl font-bold uppercase text-white mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                Premium Benefits Active
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {premium.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              {premium.premium_until && (
                <p className="text-gray-500 text-sm mt-4">
                  Premium until: {new Date(premium.premium_until).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="sessions">
            <TabsList className="bg-[#141414] border border-white/5 mb-6 flex-wrap">
              <TabsTrigger value="sessions" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Clock className="w-4 h-4 mr-2" />
                Sessions
                {sessions.filter(s => s.status === 'confirmed').length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-primary/30 rounded-full text-xs">
                    {sessions.filter(s => s.status === 'confirmed').length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="coaching" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="coaching-tab">
                <Users className="w-4 h-4 mr-2" />
                Coaching
                {coachingClients.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-primary/30 rounded-full text-xs">
                    {coachingClients.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="pricing" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Settings className="w-4 h-4 mr-2" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="packages" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <PoundSterling className="w-4 h-4 mr-2" />
                Packages
              </TabsTrigger>
              <TabsTrigger value="availability" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Calendar className="w-4 h-4 mr-2" />
                Availability
              </TabsTrigger>
              <TabsTrigger value="subscriptions" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Crown className="w-4 h-4 mr-2" />
                Subscriptions
              </TabsTrigger>
              {disputes.length > 0 && (
                <TabsTrigger value="disputes" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Disputes ({disputes.length})
                </TabsTrigger>
              )}
              {premium.is_premium && (
                <TabsTrigger value="analytics" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                  Analytics
                </TabsTrigger>
              )}
            </TabsList>

            {/* Sessions Tab - For marking attendance */}
            <TabsContent value="sessions">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">Session Attendance</h2>
                  <p className="text-sm text-gray-400">Mark sessions as completed or record no-shows. Unlock a client once for £4 to access their contact details for all future sessions.</p>
                  {isPremium && (
                    <Badge className="mt-2 bg-yellow-500/20 text-yellow-400">
                      <Crown className="w-3 h-3 mr-1" />
                      Premium: Free lead unlocks
                    </Badge>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-gray-400">Client</TableHead>
                        <TableHead className="text-gray-400">Contact</TableHead>
                        <TableHead className="text-gray-400">Session</TableHead>
                        <TableHead className="text-gray-400">Earnings</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session) => (
                        <TableRow key={session.id} className="border-white/5 hover:bg-white/5">
                          <TableCell className="text-white font-medium">{session.user_name}</TableCell>
                          <TableCell>
                            {session.lead_unlocked ? (
                              <div className="flex items-center gap-2">
                                <Unlock className="w-4 h-4 text-green-400" />
                                <a 
                                  href={`mailto:${session.user_email}`}
                                  className="text-primary hover:underline text-sm"
                                >
                                  {session.user_email}
                                </a>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/messages?to=${session.user_id}&name=${encodeURIComponent(session.user_name)}`)}
                                  className="text-gray-400 hover:text-primary p-1 h-auto"
                                  title="Send message"
                                >
                                  <MessageSquare className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnlockLead(session.user_id)}
                                disabled={unlocking === session.user_id}
                                className="border-primary text-primary hover:bg-primary/10"
                                data-testid={`unlock-lead-${session.user_id}`}
                              >
                                {unlocking === session.user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Lock className="w-3 h-3 mr-1" />
                                    {isPremium ? 'Unlock Free' : 'Unlock £4 (one-time)'}
                                  </>
                                )}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {session.session_type?.replace('session_', '').replace('min', ' min')}
                          </TableCell>
                          <TableCell className="text-primary font-semibold">
                            £{session.trainer_earnings?.toFixed(2)}
                          </TableCell>
                          <TableCell>{getStatusBadge(session.status)}</TableCell>
                          <TableCell className="text-right">
                            {session.status === 'confirmed' && (
                              <Button
                                size="sm"
                                onClick={() => setStatusDialog({ open: true, booking: session })}
                                className="bg-primary text-white hover:bg-primary/90"
                                data-testid={`update-session-${session.id}`}
                              >
                                Update Status
                              </Button>
                            )}
                            {session.status === 'disputed' && session.dispute_status === 'pending_trainer_response' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDisputeDialog({ open: true, dispute: session })}
                                className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
                              >
                                Respond
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {sessions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                            No sessions to manage
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Coaching Tab - Client & Workout Management */}
            <TabsContent value="coaching">
              <div className="space-y-6">
                {/* Add Client Section */}
                <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 border-b border-white/5">
                    <h2 className="font-semibold text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Coaching Clients
                    </h2>
                    <p className="text-sm text-gray-400">Add clients to assign workouts and track their progress</p>
                  </div>
                  <div className="p-4">
                    <div className="flex flex-col sm:flex-row gap-3 mb-4">
                      <Input
                        type="email"
                        placeholder="Client's email address"
                        value={newClientEmail}
                        onChange={(e) => setNewClientEmail(e.target.value)}
                        className="bg-[#0B0B0B] border-white/10 text-white flex-1"
                        data-testid="new-client-email"
                      />
                      <Input
                        type="text"
                        placeholder="Notes (optional)"
                        value={newClientNotes}
                        onChange={(e) => setNewClientNotes(e.target.value)}
                        className="bg-[#0B0B0B] border-white/10 text-white flex-1"
                        data-testid="new-client-notes"
                      />
                      <Button
                        onClick={addCoachingClient}
                        disabled={addingClient || !newClientEmail.trim()}
                        className="bg-primary text-white"
                        data-testid="add-client-button"
                      >
                        {addingClient ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                        Add Client
                      </Button>
                    </div>
                    
                    {/* Client List */}
                    {coachingClients.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No coaching clients yet. Add a client to get started.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {coachingClients.map((client) => (
                          <div key={client.id} className="bg-[#1A1A1A] rounded-xl border border-white/5 overflow-hidden">
                            <div 
                              className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                              onClick={() => {
                                if (expandedClient === client.id) {
                                  setExpandedClient(null);
                                  setSelectedClient(null);
                                } else {
                                  setExpandedClient(client.id);
                                  fetchClientDetails(client.id);
                                }
                              }}
                              data-testid={`client-row-${client.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                  <span className="text-primary font-semibold">
                                    {client.user_name?.charAt(0) || '?'}
                                  </span>
                                </div>
                                <div>
                                  <p className="text-white font-medium">{client.user_name || 'Unknown'}</p>
                                  <p className="text-sm text-gray-400">{client.user_email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right hidden sm:block">
                                  <p className="text-sm text-gray-400">{client.assigned_workouts || 0} assigned</p>
                                  <p className="text-sm text-green-400">{client.completed_sessions || 0} completed</p>
                                </div>
                                {expandedClient === client.id ? (
                                  <ChevronUp className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                            </div>
                            
                            {/* Expanded Client Details */}
                            {expandedClient === client.id && selectedClient && (
                              <div className="border-t border-white/5 p-4 space-y-4">
                                {/* Assign Workout */}
                                <div>
                                  <h4 className="text-sm font-medium text-gray-300 mb-2">Assign Workout</h4>
                                  {coachingWorkouts.length === 0 ? (
                                    <p className="text-sm text-gray-500">Create a workout first to assign it.</p>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {coachingWorkouts.map((workout) => (
                                        <Button
                                          key={workout.id}
                                          size="sm"
                                          variant="outline"
                                          onClick={() => assignWorkout(client.id, workout.id)}
                                          disabled={assigningWorkout === `${client.id}-${workout.id}`}
                                          className="border-primary/50 text-primary hover:bg-primary/10"
                                          data-testid={`assign-workout-${workout.id}`}
                                        >
                                          {assigningWorkout === `${client.id}-${workout.id}` ? (
                                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                          ) : (
                                            <Plus className="w-3 h-3 mr-1" />
                                          )}
                                          {workout.name}
                                        </Button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Current Assignments */}
                                {selectedClient.assignments?.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2">Current Assignments</h4>
                                    <div className="space-y-2">
                                      {selectedClient.assignments.map((assignment) => (
                                        <div key={assignment.id} className="flex items-center justify-between bg-[#0B0B0B] rounded-lg p-3">
                                          <div>
                                            <p className="text-white text-sm">{assignment.workout_name}</p>
                                            <p className="text-xs text-gray-500">
                                              Assigned {new Date(assignment.assigned_at).toLocaleDateString()}
                                            </p>
                                          </div>
                                          <Badge className={assignment.status === 'assigned' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}>
                                            {assignment.status}
                                          </Badge>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Completed Sessions */}
                                {selectedClient.completed_sessions?.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                                      <History className="w-4 h-4" />
                                      Recent Workout History
                                    </h4>
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                      {selectedClient.completed_sessions.slice(0, 5).map((session) => (
                                        <div key={session.id} className="bg-[#0B0B0B] rounded-lg p-3">
                                          <div className="flex items-center justify-between mb-2">
                                            <p className="text-white text-sm font-medium">{session.workout_name}</p>
                                            <span className="text-xs text-gray-500">
                                              {new Date(session.completed_at).toLocaleDateString()}
                                            </span>
                                          </div>
                                          <div className="text-xs text-gray-400">
                                            {session.sets?.length || 0} sets logged
                                            {session.sets?.length > 0 && (
                                              <span className="ml-2">
                                                • Total volume: {session.sets.reduce((sum, s) => sum + (s.weight * s.reps), 0).toFixed(0)}kg
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Workout Templates Section */}
                <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 border-b border-white/5 flex items-center justify-between">
                    <div>
                      <h2 className="font-semibold text-white flex items-center gap-2">
                        <Dumbbell className="w-5 h-5 text-primary" />
                        My Workouts
                      </h2>
                      <p className="text-sm text-gray-400">Create workout templates to assign to clients</p>
                    </div>
                    <Button
                      onClick={() => setShowWorkoutBuilder(true)}
                      className="bg-primary text-white"
                      data-testid="create-workout-button"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New Workout
                    </Button>
                  </div>
                  
                  {/* Workout Builder */}
                  {showWorkoutBuilder && (
                    <div className="p-4 border-b border-white/5 bg-[#1A1A1A]">
                      <div className="space-y-4">
                        <Input
                          placeholder="Workout Name (e.g., Upper Body A)"
                          value={workoutName}
                          onChange={(e) => setWorkoutName(e.target.value)}
                          className="bg-[#0B0B0B] border-white/10 text-white"
                          data-testid="workout-name-input"
                        />
                        
                        <div className="space-y-3">
                          {workoutExercises.map((exercise, index) => (
                            <div key={index} className="bg-[#0B0B0B] rounded-xl p-4 border border-white/5">
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                                <div className="col-span-2 sm:col-span-1">
                                  <label className="text-xs text-gray-400 block mb-1">Exercise</label>
                                  <Input
                                    placeholder="Exercise name"
                                    value={exercise.name}
                                    onChange={(e) => updateExercise(index, 'name', e.target.value)}
                                    className="bg-[#141414] border-white/10 text-white"
                                    data-testid={`exercise-name-${index}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-gray-400 block mb-1">Sets</label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={exercise.sets}
                                    onChange={(e) => updateExercise(index, 'sets', e.target.value)}
                                    className="bg-[#141414] border-white/10 text-white"
                                    data-testid={`exercise-sets-${index}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-gray-400 block mb-1">Reps</label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={exercise.target_reps}
                                    onChange={(e) => updateExercise(index, 'target_reps', e.target.value)}
                                    className="bg-[#141414] border-white/10 text-white"
                                    data-testid={`exercise-reps-${index}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-gray-400 block mb-1">Weight (kg)</label>
                                  <Input
                                    type="number"
                                    placeholder="Optional"
                                    value={exercise.target_weight}
                                    onChange={(e) => updateExercise(index, 'target_weight', e.target.value)}
                                    className="bg-[#141414] border-white/10 text-white"
                                    data-testid={`exercise-weight-${index}`}
                                  />
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeExercise(index)}
                                    disabled={workoutExercises.length <= 1}
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Input
                                  placeholder="Notes (optional)"
                                  value={exercise.notes}
                                  onChange={(e) => updateExercise(index, 'notes', e.target.value)}
                                  className="bg-[#141414] border-white/10 text-white text-sm"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Button
                            variant="outline"
                            onClick={addExercise}
                            className="border-white/10 text-gray-300"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Exercise
                          </Button>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setShowWorkoutBuilder(false);
                                setWorkoutName('');
                                setWorkoutExercises([{ name: '', sets: 3, target_reps: 10, target_weight: '', notes: '' }]);
                              }}
                              className="border-white/10 text-gray-300"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={saveWorkout}
                              disabled={savingWorkout || !workoutName.trim()}
                              className="bg-green-600 text-white hover:bg-green-700"
                              data-testid="save-workout-button"
                            >
                              {savingWorkout ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                              Save Workout
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Workout List */}
                  <div className="p-4">
                    {coachingWorkouts.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Dumbbell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No workouts yet. Create your first workout template.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {coachingWorkouts.map((workout) => (
                          <div key={workout.id} className="bg-[#1A1A1A] rounded-xl p-4 border border-white/5">
                            <div className="flex items-start justify-between mb-3">
                              <h3 className="font-semibold text-white">{workout.name}</h3>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteWorkout(workout.id)}
                                className="text-gray-400 hover:text-red-400 h-8 w-8"
                                data-testid={`delete-workout-${workout.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="space-y-1">
                              {workout.exercises?.slice(0, 4).map((ex, idx) => (
                                <p key={idx} className="text-sm text-gray-400">
                                  • {ex.name} - {ex.sets}×{ex.target_reps}
                                </p>
                              ))}
                              {workout.exercises?.length > 4 && (
                                <p className="text-sm text-gray-500">+{workout.exercises.length - 4} more</p>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-3">
                              Created {new Date(workout.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Pricing Tab - Set Your Session Prices */}
            <TabsContent value="pricing">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">My Session Pricing</h2>
                  <p className="text-sm text-gray-400">Set your own session durations and prices. Clients will see these options when booking.</p>
                </div>
                <div className="p-4 space-y-4">
                  {sessionOfferings.length === 0 ? (
                    <div className="text-center py-8">
                      <PoundSterling className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400 mb-4">You haven't set up any session offerings yet.</p>
                      <Button onClick={addSessionOffering} className="bg-primary text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your First Session
                      </Button>
                    </div>
                  ) : (
                    <>
                      {sessionOfferings.map((offering, index) => (
                        <div key={offering.id} className="bg-[#1A1A1A] rounded-xl p-4 border border-white/5">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Duration (mins)</label>
                              <Input
                                type="number"
                                min="15"
                                max="180"
                                value={offering.duration}
                                onChange={(e) => updateOffering(index, 'duration', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Price (£)</label>
                              <Input
                                type="number"
                                min="5"
                                max="500"
                                step="0.01"
                                value={offering.price}
                                onChange={(e) => updateOffering(index, 'price', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Label (optional)</label>
                              <Input
                                type="text"
                                placeholder={`${offering.duration} Minutes`}
                                value={offering.label}
                                onChange={(e) => updateOffering(index, 'label', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeOffering(index)}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Your earnings: £{(offering.price * 0.9).toFixed(2)} (after 10% platform fee)
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button
                          variant="outline"
                          onClick={addSessionOffering}
                          className="border-white/20 text-gray-300 hover:bg-white/5"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Another Session
                        </Button>
                        <Button
                          onClick={saveSessionPricing}
                          disabled={savingPricing}
                          className="bg-primary text-white"
                        >
                          {savingPricing ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Save Pricing
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Packages Tab */}
            <TabsContent value="packages">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">Session Packages</h2>
                  <p className="text-sm text-gray-400">Create bundle deals to attract committed clients. Offer discounts for bulk sessions.</p>
                </div>
                <div className="p-4 space-y-4">
                  {packageOfferings.length === 0 ? (
                    <div className="text-center py-8">
                      <PoundSterling className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400 mb-4">No packages yet. Create bundles to boost bookings.</p>
                      <Button onClick={addPackage} className="bg-primary text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Package
                      </Button>
                    </div>
                  ) : (
                    <>
                      {packageOfferings.map((pkg, index) => (
                        <div key={pkg.id} className="bg-[#1A1A1A] rounded-xl p-4 border border-white/5">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Package Name</label>
                              <Input
                                type="text"
                                placeholder="5-Session Pack"
                                value={pkg.name}
                                onChange={(e) => updatePackage(index, 'name', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1"># Sessions</label>
                              <Input
                                type="number"
                                min="2"
                                max="50"
                                value={pkg.session_count}
                                onChange={(e) => updatePackage(index, 'session_count', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Mins/Session</label>
                              <Input
                                type="number"
                                min="15"
                                max="180"
                                value={pkg.session_duration}
                                onChange={(e) => updatePackage(index, 'session_duration', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Total Price (£)</label>
                              <Input
                                type="number"
                                min="10"
                                max="5000"
                                value={pkg.total_price}
                                onChange={(e) => updatePackage(index, 'total_price', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removePackage(index)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Per session: £{(pkg.total_price / pkg.session_count).toFixed(2)} • Your earnings: £{(pkg.total_price * 0.9).toFixed(2)}
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button variant="outline" onClick={addPackage} className="border-white/20 text-gray-300">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Package
                        </Button>
                        <Button onClick={savePackages} disabled={savingPackages} className="bg-primary text-white">
                          {savingPackages ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          Save Packages
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Availability Tab */}
            <TabsContent value="availability">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">Weekly Availability</h2>
                  <p className="text-sm text-gray-400">Set your regular working hours. Clients will see available slots when booking.</p>
                </div>
                <div className="p-4 space-y-4">
                  {availability.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400 mb-4">Set your availability so clients can book at convenient times.</p>
                      <Button onClick={addAvailabilitySlot} className="bg-primary text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Time Slot
                      </Button>
                    </div>
                  ) : (
                    <>
                      {availability.map((slot, index) => (
                        <div key={index} className="bg-[#1A1A1A] rounded-xl p-4 border border-white/5">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Day</label>
                              <select
                                value={slot.day_of_week}
                                onChange={(e) => updateAvailabilitySlot(index, 'day_of_week', e.target.value)}
                                className="w-full h-10 px-3 bg-[#0B0B0B] border border-white/10 rounded-md text-white"
                              >
                                {DAYS.map((day, i) => (
                                  <option key={i} value={i}>{day}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Start Time</label>
                              <Input
                                type="time"
                                value={slot.start_time}
                                onChange={(e) => updateAvailabilitySlot(index, 'start_time', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">End Time</label>
                              <Input
                                type="time"
                                value={slot.end_time}
                                onChange={(e) => updateAvailabilitySlot(index, 'end_time', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeAvailabilitySlot(index)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button variant="outline" onClick={addAvailabilitySlot} className="border-white/20 text-gray-300">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Time Slot
                        </Button>
                        <Button onClick={saveAvailability} disabled={savingAvailability} className="bg-primary text-white">
                          {savingAvailability ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          Save Availability
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Subscriptions Tab */}
            <TabsContent value="subscriptions">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">Subscription Plans</h2>
                  <p className="text-sm text-gray-400">Offer monthly training plans with recurring payments. Great for regular clients.</p>
                </div>
                <div className="p-4 space-y-4">
                  {subscriptionPlans.length === 0 ? (
                    <div className="text-center py-8">
                      <Crown className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400 mb-4">Create subscription plans for recurring revenue.</p>
                      <Button onClick={addSubscriptionPlan} className="bg-primary text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Subscription Plan
                      </Button>
                    </div>
                  ) : (
                    <>
                      {subscriptionPlans.map((plan, index) => (
                        <div key={plan.id} className="bg-[#1A1A1A] rounded-xl p-4 border border-white/5">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Plan Name</label>
                              <Input
                                type="text"
                                placeholder="Weekly Training"
                                value={plan.name}
                                onChange={(e) => updateSubscriptionPlan(index, 'name', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Sessions/Month</label>
                              <Input
                                type="number"
                                min="1"
                                max="30"
                                value={plan.sessions_per_month}
                                onChange={(e) => updateSubscriptionPlan(index, 'sessions_per_month', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Mins/Session</label>
                              <Input
                                type="number"
                                min="15"
                                max="180"
                                value={plan.session_duration}
                                onChange={(e) => updateSubscriptionPlan(index, 'session_duration', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-400 block mb-1">Monthly Price (£)</label>
                              <Input
                                type="number"
                                min="20"
                                max="2000"
                                value={plan.monthly_price}
                                onChange={(e) => updateSubscriptionPlan(index, 'monthly_price', e.target.value)}
                                className="bg-[#0B0B0B] border-white/10 text-white"
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeSubscriptionPlan(index)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Per session: £{(plan.monthly_price / plan.sessions_per_month).toFixed(2)} • Your earnings: £{(plan.monthly_price * 0.9).toFixed(2)}/month
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button variant="outline" onClick={addSubscriptionPlan} className="border-white/20 text-gray-300">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Plan
                        </Button>
                        <Button onClick={saveSubscriptionPlans} disabled={savingSubscriptions} className="bg-primary text-white">
                          {savingSubscriptions ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          Save Plans
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bookings">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">Recent Bookings</h2>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-gray-400">Client</TableHead>
                        <TableHead className="text-gray-400">Session</TableHead>
                        <TableHead className="text-gray-400">Amount</TableHead>
                        <TableHead className="text-gray-400">Your Earnings</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recent_bookings?.map((booking) => (
                        <TableRow key={booking.id} className="border-white/5 hover:bg-white/5">
                          <TableCell className="text-white">{booking.user_name}</TableCell>
                          <TableCell className="text-gray-400">{booking.session_type?.replace('session_', '').replace('min', ' min')}</TableCell>
                          <TableCell className="text-white">£{booking.amount?.toFixed(2)}</TableCell>
                          <TableCell className="text-primary font-semibold">£{booking.trainer_earnings?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={
                              booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                              booking.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                              booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }>
                              {booking.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {new Date(booking.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!recent_bookings || recent_bookings.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                            No bookings yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {premium.is_premium && analytics && (
              <TabsContent value="analytics">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                    <h3 className="font-semibold text-white mb-4">Performance Overview</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Total Bookings</span>
                        <span className="text-white font-semibold">{analytics.total_bookings}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Total Reviews</span>
                        <span className="text-white font-semibold">{analytics.total_reviews}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Average Rating</span>
                        <span className="text-yellow-400 font-semibold">{analytics.average_rating?.toFixed(1)} ★</span>
                      </div>
                      {analytics.bookings_last_30_days !== undefined && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Bookings (Last 30 Days)</span>
                          <span className="text-primary font-semibold">{analytics.bookings_last_30_days}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {analytics.session_breakdown && (
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <h3 className="font-semibold text-white mb-4">Session Breakdown</h3>
                      <div className="space-y-4">
                        {Object.entries(analytics.session_breakdown).map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center">
                            <span className="text-gray-400">{type.replace('session_', '').replace('min', ' Minutes')}</span>
                            <span className="text-white font-semibold">{count} sessions</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Disputes Tab */}
            {disputes.length > 0 && (
              <TabsContent value="disputes">
                <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 border-b border-white/5">
                    <h2 className="font-semibold text-white">Pending Disputes</h2>
                    <p className="text-sm text-gray-400">Respond to client disputes to resolve them</p>
                  </div>
                  <div className="p-4 space-y-4">
                    {disputes.map((dispute) => (
                      <div key={dispute.id} className="bg-[#1A1A1A] rounded-xl p-4 border border-yellow-500/20">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{dispute.user_name}</p>
                            <p className="text-sm text-gray-400">
                              {dispute.session_type?.replace('session_', '').replace('min', ' min')} session
                            </p>
                            <p className="text-sm text-yellow-400 mt-2">
                              Please respond to this dispute within 24 hours
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => setDisputeDialog({ open: true, dispute })}
                            className="bg-yellow-500 text-black hover:bg-yellow-400"
                          >
                            Respond Now
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            )}
          </Tabs>

          {/* Session Status Update Dialog */}
          <Dialog open={statusDialog.open} onOpenChange={(open) => setStatusDialog({ open, booking: statusDialog.booking })}>
            <DialogContent className="bg-[#141414] border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">
                  Update Session Status
                </DialogTitle>
              </DialogHeader>
              {statusDialog.booking && (
                <div className="space-y-4 mt-4">
                  <div className="bg-[#1A1A1A] rounded-xl p-4">
                    <p className="text-white font-medium">{statusDialog.booking.user_name}</p>
                    <p className="text-sm text-gray-400">
                      {statusDialog.booking.session_type?.replace('session_', '').replace('min', ' min')} session
                    </p>
                    <p className="text-sm text-primary mt-1">
                      Earnings: £{statusDialog.booking.trainer_earnings?.toFixed(2)}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-gray-400">Notes (optional)</label>
                    <Textarea
                      value={statusNotes}
                      onChange={(e) => setStatusNotes(e.target.value)}
                      placeholder="Add any notes about this session..."
                      className="bg-[#1A1A1A] border-transparent text-white"
                    />
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={() => handleUpdateSessionStatus(statusDialog.booking.id, 'completed')}
                      disabled={updating}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid="mark-completed"
                    >
                      {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Session Completed
                    </Button>
                    <Button
                      onClick={() => handleUpdateSessionStatus(statusDialog.booking.id, 'user_no_show')}
                      disabled={updating}
                      variant="outline"
                      className="w-full border-orange-500 text-orange-400 hover:bg-orange-500/10"
                      data-testid="mark-user-no-show"
                    >
                      {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                      User No-Show (50% Refund)
                    </Button>
                    <Button
                      onClick={() => handleUpdateSessionStatus(statusDialog.booking.id, 'trainer_missed')}
                      disabled={updating}
                      variant="outline"
                      className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
                      data-testid="mark-trainer-missed"
                    >
                      {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4 mr-2" />}
                      I Missed This Session (Full Refund)
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    Users have 2 hours after this action to dispute the outcome
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Dispute Response Dialog */}
          <Dialog open={disputeDialog.open} onOpenChange={(open) => setDisputeDialog({ open, dispute: disputeDialog.dispute })}>
            <DialogContent className="bg-[#141414] border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">
                  Respond to Dispute
                </DialogTitle>
              </DialogHeader>
              {disputeDialog.dispute && (
                <div className="space-y-4 mt-4">
                  <div className="bg-[#1A1A1A] rounded-xl p-4">
                    <p className="text-white font-medium">{disputeDialog.dispute.user_name}</p>
                    <p className="text-sm text-gray-400">
                      {disputeDialog.dispute.session_type?.replace('session_', '').replace('min', ' min')} session
                    </p>
                    <p className="text-sm text-yellow-400 mt-2">
                      Status was marked as: {disputeDialog.dispute.status?.replace('_', ' ')}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-gray-400">Your Response *</label>
                    <Textarea
                      value={disputeResponse}
                      onChange={(e) => setDisputeResponse(e.target.value)}
                      placeholder="Explain what happened during this session..."
                      className="bg-[#1A1A1A] border-transparent text-white min-h-[100px]"
                    />
                  </div>

                  <Button
                    onClick={() => handleRespondToDispute(disputeDialog.dispute.dispute_id)}
                    disabled={updating || !disputeResponse.trim()}
                    className="w-full bg-primary text-white"
                  >
                    {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                    Submit Response
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    After you respond, an admin will review and make the final decision
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
